import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@material-ui/core';
import {
  Visibility,
  VisibilityOff,
  Instagram,
  Phone,
  Email,
  Person,
  CreditCard,
  LockOutlined,
  CheckCircleOutline,
  TrendingUp,
  Link as LinkIcon,
  Assessment,
  Payment,
  Add,
  Delete
} from '@material-ui/icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api-production-4f9e.up.railway.app';

const COLORS = {
  primary: '#6950A2',
  primaryDark: '#4B3880',
  primaryLight: '#9B7FD4',
  accent: '#E91E8C',
  accentLight: '#FF6FC8',
  bg: '#FAF7FF',
  bgCard: '#FFFFFF',
  text: '#1A1A2E',
  textSecondary: '#6B6B8A',
  pink: '#FFE8F5',
  purple: '#F0EAFF'
};

const benefits = [
  {
    icon: <TrendingUp style={{ fontSize: 32, color: COLORS.primary }} />,
    title: 'Comissões Atrativas',
    desc: 'Ganhe até 10% em cada venda realizada pelo seu link'
  },
  {
    icon: <LinkIcon style={{ fontSize: 32, color: COLORS.primary }} />,
    title: 'Link Exclusivo',
    desc: 'Seu link personalizado para divulgar nas redes sociais'
  },
  {
    icon: <Assessment style={{ fontSize: 32, color: COLORS.primary }} />,
    title: 'Painel Completo',
    desc: 'Acompanhe suas vendas e comissões em tempo real'
  },
  {
    icon: <Payment style={{ fontSize: 32, color: COLORS.primary }} />,
    title: 'Pagamento via PIX',
    desc: 'Receba suas comissões de forma rápida e segura'
  }
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  cpf: '',
  about: '',
  password: '',
  channels: []
};

const PLATFORM_OPTIONS = [
  { value: 'instagram', label: 'Instagram', icon: <Instagram /> },
  { value: 'tiktok', label: 'TikTok', icon: <LinkIcon /> },
  { value: 'youtube', label: 'YouTube', icon: <LinkIcon /> },
  { value: 'facebook', label: 'Facebook', icon: <LinkIcon /> },
  { value: 'other', label: 'Outro', icon: <LinkIcon /> }
];

function maskPhone(val) {
  const v = val.replace(/\D/g, '').slice(0, 11);
  if (v.length > 6) return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  if (v.length > 2) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  return v;
}

function maskCPF(val) {
  const v = val.replace(/\D/g, '').slice(0, 11);
  if (v.length > 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
  if (v.length > 6) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
  if (v.length > 3) return `${v.slice(0, 3)}.${v.slice(3)}`;
  return v;
}

export default function LandingPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let masked = value;
    if (name === 'phone') masked = maskPhone(value);
    if (name === 'cpf') masked = maskCPF(value);
    setForm((prev) => ({ ...prev, [name]: masked }));
  };

  const handleAddChannel = () => {
    setForm((prev) => ({
      ...prev,
      channels: [...prev.channels, { id: Date.now(), platform: 'instagram', name: '', url: '' }]
    }));
  };

  const handleRemoveChannel = (index) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.filter((_, i) => i !== index)
    }));
  };

  const handleChannelChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.map((ch, i) => (i === index ? { ...ch, [field]: value } : ch))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/users`, form, {
        headers: { 'Content-Type': 'application/json' }
      });
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data) && err?.response?.data[0]?.message) ||
        'Erro ao cadastrar. Verifique os dados e tente novamente.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Seja uma Afiliada | Diva Cosméticos</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Poppins:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.bg, fontFamily: 'Poppins, sans-serif' }}>
        {/* HEADER */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
            py: 2,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 2px 20px rgba(105,80,162,0.4)'
          }}
        >
          <Container maxWidth="lg">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <img src="/logo-branca.png" alt="Diva Cosméticos" style={{ height: 40 }} />
              <Button
                variant="outlined"
                size="small"
                href="/login"
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 20, textTransform: 'none' }}
              >
                Já sou afiliada
              </Button>
            </Box>
          </Container>
        </Box>

        {/* HERO */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.pink} 60%, ${COLORS.bg} 100%)`,
            py: { xs: 8, md: 12 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${COLORS.primaryLight}22, transparent)`
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -40,
              left: -40,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${COLORS.accentLight}22, transparent)`
            }}
          />

          <Container maxWidth="md">
            <Chip
              label="✨ Programa de Afiliadas"
              style={{
                background: COLORS.primary,
                color: '#fff',
                marginBottom: 20,
                fontFamily: 'Poppins',
                fontWeight: 500
              }}
            />
            <Typography
              variant="h2"
              style={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                color: COLORS.text,
                marginBottom: 20,
                lineHeight: 1.2,
                fontSize: 'clamp(2rem, 5vw, 3.5rem)'
              }}
            >
              Transforme sua <span style={{ color: COLORS.primary }}>paixão por beleza</span> em renda
            </Typography>
            <Typography
              variant="h6"
              style={{
                color: COLORS.textSecondary,
                fontWeight: 400,
                maxWidth: 600,
                margin: '0 auto 40px',
                lineHeight: 1.7
              }}
            >
              Junte-se ao programa de afiliadas Diva Cosméticos e ganhe comissões indicando os melhores produtos de
              beleza para suas amigas e seguidoras.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="#cadastro"
              style={{
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.primary})`,
                color: '#fff',
                borderRadius: 50,
                padding: '14px 40px',
                fontSize: 16,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 8px 30px ${COLORS.accent}55`,
                fontFamily: 'Poppins'
              }}
            >
              Quero ser Afiliada 💜
            </Button>
          </Container>
        </Box>

        {/* BENEFÍCIOS */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography
            variant="h4"
            align="center"
            style={{
              fontFamily: 'Playfair Display, serif',
              fontWeight: 700,
              color: COLORS.text,
              marginBottom: 8
            }}
          >
            Por que ser afiliada Diva?
          </Typography>
          <Typography align="center" style={{ color: COLORS.textSecondary, marginBottom: 48 }}>
            Vantagens exclusivas para quem faz parte do nosso programa
          </Typography>

          <Grid container spacing={4}>
            {benefits.map((b) => (
              <Grid item xs={12} sm={6} md={3} key={b.title}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 32,
                    textAlign: 'center',
                    borderRadius: 20,
                    border: `1px solid ${COLORS.purple}`,
                    background: COLORS.bgCard,
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      background: COLORS.purple,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}
                  >
                    {b.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 600, color: COLORS.text, marginBottom: 8, fontSize: 15 }}
                  >
                    {b.title}
                  </Typography>
                  <Typography variant="body2" style={{ color: COLORS.textSecondary, lineHeight: 1.7 }}>
                    {b.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Divider style={{ background: COLORS.purple, margin: '0 40px' }} />

        {/* FORMULÁRIO */}
        <Box id="cadastro" sx={{ py: 8, background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.pink} 100%)` }}>
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              style={{
                padding: 48,
                borderRadius: 24,
                background: COLORS.bgCard,
                boxShadow: '0 20px 80px rgba(105,80,162,0.12)',
                border: `1px solid ${COLORS.purple}`
              }}
            >
              <Typography
                variant="h4"
                align="center"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 700,
                  color: COLORS.text,
                  marginBottom: 8
                }}
              >
                Cadastre-se Agora
              </Typography>
              <Typography align="center" style={{ color: COLORS.textSecondary, marginBottom: 32, fontSize: 14 }}>
                Preencha seus dados e aguarde a aprovação da nossa equipe
              </Typography>

              {success ? (
                <Box textAlign="center" py={4}>
                  <CheckCircleOutline style={{ fontSize: 64, color: '#4CAF50', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: COLORS.text, fontWeight: 600, marginBottom: 8 }}>
                    Cadastro realizado! 🎉
                  </Typography>
                  <Typography style={{ color: COLORS.textSecondary, fontSize: 14 }}>
                    Nossa equipe analisará seu perfil e você receberá um contato em breve. Bem-vinda à família Diva! 💜
                  </Typography>
                </Box>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && (
                    <Alert severity="error" style={{ marginBottom: 24, borderRadius: 12 }}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome Completo *"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person style={{ color: COLORS.primary }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="E-mail *"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email style={{ color: COLORS.primary }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="WhatsApp *"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        placeholder="(00) 00000-0000"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone style={{ color: COLORS.primary }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="CPF *"
                        name="cpf"
                        value={form.cpf}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        placeholder="000.000.000-00"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CreditCard style={{ color: COLORS.primary }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" style={{ fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>
                        Suas Redes Sociais
                      </Typography>
                      <Typography variant="body2" style={{ color: COLORS.textSecondary, marginBottom: 16 }}>
                        Adicione os canais onde você vai divulgar os produtos
                      </Typography>

                      {form.channels.map((channel, index) => (
                        <Box
                          key={channel.id}
                          sx={{ mb: 2, p: 2, border: `1px solid ${COLORS.purple}`, borderRadius: 3 }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth variant="outlined">
                                <InputLabel>Plataforma</InputLabel>
                                <Select
                                  value={channel.platform}
                                  onChange={(e) => handleChannelChange(index, 'platform', e.target.value)}
                                  label="Plataforma"
                                  sx={{ borderRadius: 3 }}
                                >
                                  {PLATFORM_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                label="Nome do Canal"
                                value={channel.name}
                                onChange={(e) => handleChannelChange(index, 'name', e.target.value)}
                                variant="outlined"
                                placeholder="Ex: @seuperfil"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Link/URL"
                                value={channel.url}
                                onChange={(e) => handleChannelChange(index, 'url', e.target.value)}
                                variant="outlined"
                                placeholder="https://..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={1} style={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton onClick={() => handleRemoveChannel(index)} style={{ color: COLORS.accent }}>
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}

                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddChannel}
                        style={{
                          borderColor: COLORS.primary,
                          color: COLORS.primary,
                          borderRadius: 12,
                          textTransform: 'none'
                        }}
                      >
                        Adicionar Canal
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Por que você quer ser afiliada Diva?"
                        name="about"
                        value={form.about}
                        onChange={handleChange}
                        variant="outlined"
                        multiline
                        rows={3}
                        placeholder="Conte um pouco sobre você..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Senha *"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        placeholder="Mínimo 6 caracteres"
                        InputProps={{
                          inputProps: { minLength: 6 },
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined style={{ color: COLORS.primary }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                          },
                          '& label.Mui-focused': { color: COLORS.primary }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        style={{
                          background: loading ? '#CCC' : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.primary})`,
                          color: '#fff',
                          borderRadius: 12,
                          padding: '14px',
                          fontSize: 16,
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: loading ? 'none' : `0 8px 24px ${COLORS.primary}44`,
                          fontFamily: 'Poppins'
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} style={{ color: '#fff' }} />
                        ) : (
                          'Quero ser Afiliada Diva 💜'
                        )}
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        align="center"
                        display="block"
                        style={{ color: COLORS.textSecondary }}
                      >
                        Ao se cadastrar, você concorda com nossos termos de uso. Sua conta passará por aprovação da
                        nossa equipe.
                      </Typography>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Paper>
          </Container>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
            py: 4,
            textAlign: 'center'
          }}
        >
          <Typography style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
            © {new Date().getFullYear()} Diva Cosméticos · Todos os direitos reservados
          </Typography>
          <Typography style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 4 }}>
            Desenvolvido por TecWorks Soluções em T.I.
          </Typography>
        </Box>
      </Box>
    </>
  );
}
